import { Fragment, type ReactNode } from 'react'

export type FeatureComparisonColumn = {
  key: string
  /** The `<th>` classes. Every column is one header cell, the corner included. */
  className?: string
  children?: ReactNode
}

type FeatureComparisonCell = {
  className?: string
  children?: ReactNode
}

type FeatureComparisonRow = {
  key: string
  className?: string
  /** One cell per column, the row label first. */
  cells: ReadonlyArray<FeatureComparisonCell>
}

export type FeatureComparisonSection = {
  key: string
  /** The heading row: one cell spanning every column. */
  heading: FeatureComparisonCell & { rowClassName?: string }
  rows: ReadonlyArray<FeatureComparisonRow>
}

type FeatureComparisonTableProps = {
  /** Table classes — verbatim, so a caller owns its layout (`-mt-16` for the sticky pricing head). */
  className?: string
  headClassName?: string
  headRowClassName?: string
  columns: ReadonlyArray<FeatureComparisonColumn>
  sections: ReadonlyArray<FeatureComparisonSection>
}

/**
 * The one marketing comparison table: a header row of columns, then sections
 * whose heading spans every column, then feature rows with one cell per
 * column. `/compare/<competitor>` (Brew vs one competitor, yes/limited/no) and
 * `/pricing` (one column per plan, sticky head, mobile plan picker) both
 * render through it; each caller keeps its own cell content and classes.
 *
 * The Fragment owns each section's key on purpose: a bare `<>…</>` with the
 * key on the inner `<tr>` was the source of "Each child in a list should have
 * a unique key prop" warnings on `/compare/[competitor]`.
 */
export function FeatureComparisonTable({
  className = 'w-full border-collapse',
  headClassName,
  headRowClassName,
  columns,
  sections,
}: FeatureComparisonTableProps) {
  return (
    <table className={className}>
      <thead className={headClassName}>
        <tr className={headRowClassName}>
          {columns.map((column) => (
            <th key={column.key} className={column.className}>
              {column.children}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sections.map((section) => (
          <Fragment key={section.key}>
            <tr className={section.heading.rowClassName}>
              <td
                colSpan={columns.length}
                className={section.heading.className}
              >
                {section.heading.children}
              </td>
            </tr>
            {section.rows.map((row) => (
              <tr key={row.key} className={row.className}>
                {row.cells.map((cell, index) => (
                  <td
                    // biome-ignore lint/suspicious/noArrayIndexKey: a cell's identity IS its column position
                    key={index}
                    className={cell.className}
                  >
                    {cell.children}
                  </td>
                ))}
              </tr>
            ))}
          </Fragment>
        ))}
      </tbody>
    </table>
  )
}
